import { useQuery } from "@tanstack/react-query";
import type { Session } from "@supabase/supabase-js";

import { supabase } from "./supabase";

export type GovernanceRole =
  | "project_owner"
  | "implementation_lead"
  | "assigned_reviewer"
  | "designated_reviewer";

export async function getCurrentRole(): Promise<GovernanceRole | null> {
  const { data } = await supabase.auth.getSession();
  const role = data.session?.user.app_metadata?.role;
  return isGovernanceRole(role) ? role : null;
}

function isGovernanceRole(value: unknown): value is GovernanceRole {
  return (
    value === "project_owner" ||
    value === "implementation_lead" ||
    value === "assigned_reviewer" ||
    value === "designated_reviewer"
  );
}

/**
 * Reactive session hook. main.tsx invalidates the `["session"]` query key
 * on every Supabase `onAuthStateChange` event, so components that call
 * this hook re-render on sign-in / sign-out without having to subscribe
 * to the auth listener themselves.
 */
export function useSession() {
  return useQuery<Session | null>({
    queryKey: ["session"],
    queryFn: async () => {
      const { data } = await supabase.auth.getSession();
      return data.session;
    },
    staleTime: Infinity,
  });
}
